import { area, convertArea } from '@turf/turf'
import { getBoundaryApi, getSoilSampleDatasetApi } from 'api-validations/locutus'
import {
  lassoContinuousHistogramApi,
  type LassoRequestBody,
  lassoSummaryApi,
  lassoZoneScoresApi,
  lassoZonesApi
} from 'api-validations/stitch'
import * as Comlink from 'comlink'
import type { FeatureCollection } from 'geojson'
import { useOrgCodeOrThrow } from 'shared-ui/active-operation'
import {
  keepPreviousData,
  useApiClient,
  useIsFetching,
  useQuery,
  useQueryClient
} from 'shared-ui/api-provider'
import { useStitchRequestType } from 'shared-ui/field-data'
import {
  type AsAppliedLayer,
  fetchSoilSurvey,
  interpolateSoilSurface,
  isCategoricalSoilSurveyAttribute,
  nccpiValues,
  type SoilSurface,
  soilSurfaceValues,
  type SoilSurvey,
  type SoilSurveyAttribute,
  type SoilSurveyClass,
  soilSurveyClasses,
  type SoilSurveyLegendEntry,
  zoneNccpi,
  zoneSoilSurface
} from 'shared-ui/map'
import { recommendZoneCount, type ZoningScore } from 'shared-ui/planning'
import {
  BreakMethod,
  breaksBetweenValues,
  Geo,
  isNil,
  sum,
  type ValueHistogram,
  type ValueZone,
  varianceExplained,
  type WeightedValue,
  weightedHistogram,
  type ZoneBreaks,
  zoneBreaks,
  ZoneBreaksFailure,
  zonesForBreaks
} from 'utils'
import type { PolyLikeFeature, PolyLikeGeometry } from 'utils/geo/types'
import type { z } from 'zod'

import type { PlaceZonesRequest, WorkerApi } from './Geo.worker'
// oxlint-disable-next-line import/default -- Vite virtual module (?worker&url) resolved at build time
import workerUrl from './Geo.worker?worker&url'

export enum ZoneSourceKind {
  AsApplied = 'asApplied',
  SoilSample = 'soilSample',
  SoilSurvey = 'soilSurvey'
}

export type ZoneSource =
  | ({ kind: ZoneSourceKind.AsApplied } & Pick<
      AsAppliedLayer,
      'streamId' | 'applicationId' | 'layerId'
    >)
  | { kind: ZoneSourceKind.SoilSample; samplingEventId: string; measurement: string }
  | { kind: ZoneSourceKind.SoilSurvey; attribute: SoilSurveyAttribute }

export enum ZoneSourceFailure {
  Uninterpolable = 'uninterpolable'
}

export type ZoneLayerFailure = ZoneBreaksFailure | ZoneSourceFailure

export enum ZoneLayerKind {
  Ranges = 'ranges',
  Classes = 'classes'
}

export enum ZoneLabelKind {
  Range = 'range',
  Class = 'class'
}

export type ZoneLabel =
  | { kind: ZoneLabelKind.Range; min: number; max: number }
  | { kind: ZoneLabelKind.Class; entry: SoilSurveyLegendEntry }

export const CANDIDATE_ZONE_COUNTS = [2, 3, 4, 5, 6]
export const DEFAULT_BREAK_METHOD = BreakMethod.NaturalBreaks

const HISTOGRAM_BINS = 1000
export enum SmallestZone {
  QuarterAcre = 'quarterAcre',
  HalfAcre = 'halfAcre',
  OneAcre = 'oneAcre',
  TwoAcres = 'twoAcres'
}

export const SMALLEST_ZONES = [
  SmallestZone.QuarterAcre,
  SmallestZone.HalfAcre,
  SmallestZone.OneAcre,
  SmallestZone.TwoAcres
]

export const ZONE_SIZES: Record<
  SmallestZone,
  { cellSize: number; minZoneSize: number; minFeature: number; simplify?: number }
> = {
  [SmallestZone.QuarterAcre]: { cellSize: 3, minZoneSize: 0.25, minFeature: 0.25 },
  [SmallestZone.HalfAcre]: { cellSize: 4, minZoneSize: 0.5, minFeature: 0.5 },
  [SmallestZone.OneAcre]: { cellSize: 6, minZoneSize: 1, minFeature: 1, simplify: 12 },
  [SmallestZone.TwoAcres]: { cellSize: 8, minZoneSize: 2, minFeature: 2, simplify: 20 }
}

export const DEFAULT_SMALLEST_ZONE = SmallestZone.HalfAcre

export enum EdgeRounding {
  Straight = 'straight',
  Rounded = 'rounded',
  VeryRounded = 'veryRounded'
}

export const EDGE_ROUNDINGS = [
  EdgeRounding.Straight,
  EdgeRounding.Rounded,
  EdgeRounding.VeryRounded
]

const ROUNDING_PASSES: Record<EdgeRounding, number> = {
  [EdgeRounding.Straight]: 0,
  [EdgeRounding.Rounded]: 2,
  [EdgeRounding.VeryRounded]: 4
}
const MIN_VARIANCE_GAIN = 0.05
const MAX_REASSIGNED_SHARE = 0.3

export enum ZoneSelectionKind {
  Recommended = 'recommended',
  Count = 'count',
  Custom = 'custom'
}

export type ZoneSelection =
  | { kind: ZoneSelectionKind.Recommended }
  | { kind: ZoneSelectionKind.Count; zoneCount: number }
  | { kind: ZoneSelectionKind.Custom; breaks: number[] }

export type CandidateScore = ZoningScore & { breaks: number[] }

type ContinuousHistogram = z.infer<typeof lassoContinuousHistogramApi.responseSchema>

const toValueHistogram = ({
  histogram,
  minimum,
  maximum
}: {
  histogram: ContinuousHistogram
  minimum: number
  maximum: number
}): ValueHistogram => {
  const continuousBins = histogram.bins.filter((bin) => !('blue' in bin))
  const gridBins = continuousBins.filter((bin) => 'minimum' in bin && 'maximum' in bin)
  const binsBelow = continuousBins.filter((bin) => !('minimum' in bin))
  const binsAbove = continuousBins.filter((bin) => !('maximum' in bin))

  return {
    minimum,
    maximum,
    counts: gridBins.map((bin) => bin.num_points),
    below: sum(binsBelow.map((bin) => bin.num_points)),
    above: sum(binsAbove.map((bin) => bin.num_points))
  }
}

type Boundary = z.infer<typeof getBoundaryApi.responseSchema>

type StitchLayerParams = {
  orgCode: string
  requestType: ReturnType<typeof useStitchRequestType>
  streamId: string
  applicationId: string
  layerId: string
}

type LocalZoning =
  | {
      kind: ZoneSourceKind.SoilSample
      values: WeightedValue[]
      surface: SoilSurface
      outline: PolyLikeFeature
    }
  | { kind: ZoneSourceKind.SoilSurvey; values: WeightedValue[]; survey: SoilSurvey }

type Zoning =
  | { kind: ZoneSourceKind.AsApplied; layerParams: StitchLayerParams; lassoBody: LassoRequestBody }
  | LocalZoning

type RangesLayer = {
  kind: ZoneLayerKind.Ranges
  boundary: Boundary
  histogram: ValueHistogram
  unit: string | undefined
  zoning: Zoning
}

type ZoneLayer =
  | RangesLayer
  | { kind: ZoneLayerKind.Classes; boundary: Boundary; classes: SoilSurveyClass[] }

type ZoneLayerResult = { ok: true; layer: ZoneLayer } | { ok: false; reason: ZoneLayerFailure }

type ZoneScore = { varianceExplained: number; reassignedShare: number }

type ZonedFeatures = { features: PolyLikeFeature[][]; score: ZoneScore }

const boundaryCollection = (boundary: Boundary) =>
  boundary.data.boundary.type === 'FeatureCollection'
    ? boundary.data.boundary
    : { type: 'FeatureCollection' as const, features: [boundary.data.boundary] }

const outlineOf = (boundary: Boundary): PolyLikeFeature => {
  const outline = Geo.normalizeCollection(boundaryCollection(boundary))
  if (isNil(outline)) throw new Error('Unable to normalize boundary')
  return outline
}

const acresOf = (features: PolyLikeFeature[]) =>
  convertArea(sum(features.map((feature) => area(feature))), 'meters', 'acres')

const rangesLayer = ({
  boundary,
  zoning
}: {
  boundary: Boundary
  zoning: LocalZoning
}): ZoneLayerResult => {
  const histogram = weightedHistogram({ values: zoning.values, bins: HISTOGRAM_BINS })
  if (!histogram.ok) return { ok: false, reason: histogram.reason }

  return {
    ok: true,
    layer: {
      kind: ZoneLayerKind.Ranges,
      boundary,
      histogram: histogram.histogram,
      unit: undefined,
      zoning
    }
  }
}

const soilSurveyLayer = ({
  boundary,
  survey,
  attribute
}: {
  boundary: Boundary
  survey: SoilSurvey
  attribute: SoilSurveyAttribute
}): ZoneLayerResult => {
  if (!isCategoricalSoilSurveyAttribute(attribute)) {
    return rangesLayer({
      boundary,
      zoning: { kind: ZoneSourceKind.SoilSurvey, values: nccpiValues(survey), survey }
    })
  }

  const classes = soilSurveyClasses({ survey, attribute }).filter(
    ({ features }) => features.length > 0
  )
  if (classes.length === 0) return { ok: false, reason: ZoneBreaksFailure.NoData }
  if (classes.length === 1) return { ok: false, reason: ZoneBreaksFailure.SingleValue }

  return { ok: true, layer: { kind: ZoneLayerKind.Classes, boundary, classes } }
}

const breakValues = (zones: ValueZone[]) => zones.slice(1).map((zone) => zone.min)

const betweenReadings = ({
  layer,
  breaks
}: {
  layer: RangesLayer
  breaks: ZoneBreaks
}): ZoneBreaks => {
  const { zoning } = layer
  if (!breaks.ok || zoning.kind === ZoneSourceKind.AsApplied) return breaks

  // Soil survey values are constant across each map unit, so a histogram edge just past a cluster
  // would label a zone "0.697 – 0.697". Any break inside a gap between readings classifies the same.
  return zonesForBreaks({
    histogram: layer.histogram,
    breaks: breaksBetweenValues({ values: zoning.values, breaks: breakValues(breaks.zones) })
  })
}

const candidateBreaks = ({ layer, method }: { layer: RangesLayer; method: BreakMethod }) =>
  CANDIDATE_ZONE_COUNTS.flatMap((zoneCount) => {
    const breaks = betweenReadings({
      layer,
      breaks: zoneBreaks({ histogram: layer.histogram, zoneCount, method })
    })
    return breaks.ok && breaks.zones.length === zoneCount
      ? [{ zoneCount, breaks: breakValues(breaks.zones) }]
      : []
  })

const recommend = (scores: ZoningScore[] | undefined) =>
  scores === undefined
    ? undefined
    : recommendZoneCount({
        scores,
        minGain: MIN_VARIANCE_GAIN,
        maxReassigned: MAX_REASSIGNED_SHARE
      })

export const useZoneTuning = ({
  fieldId,
  boundaryId,
  source,
  method,
  selection,
  smallestZone,
  edges
}: {
  fieldId: string
  boundaryId: string
  source: ZoneSource
  method: BreakMethod
  selection: ZoneSelection
  smallestZone: SmallestZone
  edges: EdgeRounding
}) => {
  const apiClient = useApiClient()
  const queryClient = useQueryClient()
  const orgCode = useOrgCodeOrThrow()
  const requestType = useStitchRequestType()

  const sourceKey = [orgCode, fieldId, boundaryId, source]

  const fetchBoundary = () =>
    queryClient.fetchQuery({
      queryKey: ['zoneTuning', 'boundary', orgCode, boundaryId],
      queryFn: () => apiClient.callApi(getBoundaryApi, { orgCode, boundaryId }),
      staleTime: Number.POSITIVE_INFINITY
    })

  const fetchSurvey = (boundary: Boundary) =>
    queryClient.fetchQuery({
      queryKey: ['zoneTuning', 'soilSurvey', orgCode, boundaryId],
      queryFn: ({ signal }) => fetchSoilSurvey({ boundary: outlineOf(boundary), signal }),
      staleTime: Number.POSITIVE_INFINITY
    })

  const stitchLayer = async ({
    boundary,
    streamId,
    applicationId,
    layerId
  }: { boundary: Boundary } & Pick<
    AsAppliedLayer,
    'streamId' | 'applicationId' | 'layerId'
  >): Promise<ZoneLayerResult> => {
    const layerParams = { orgCode, requestType, streamId, applicationId, layerId }
    const lassoBody = { polygon: boundaryCollection(boundary) }

    const summary = await apiClient.callApi(lassoSummaryApi, layerParams, lassoBody)

    if (!summary.hasData) {
      return { ok: false, reason: ZoneBreaksFailure.NoData }
    }

    const minimum = summary.minimum.filtered
    const maximum = summary.maximum.filtered

    if (maximum <= minimum) {
      return { ok: false, reason: ZoneBreaksFailure.SingleValue }
    }

    const histogram = await apiClient.callApi(
      lassoContinuousHistogramApi,
      { ...layerParams, minimum, maximum, bins: HISTOGRAM_BINS },
      lassoBody
    )

    return {
      ok: true,
      layer: {
        kind: ZoneLayerKind.Ranges,
        boundary,
        histogram: toValueHistogram({ histogram, minimum, maximum }),
        unit: summary.units,
        zoning: { kind: ZoneSourceKind.AsApplied, layerParams, lassoBody }
      }
    }
  }

  const soilSampleLayer = async ({
    boundary,
    samplingEventId,
    measurement
  }: {
    boundary: Boundary
    samplingEventId: string
    measurement: string
  }): Promise<ZoneLayerResult> => {
    const dataset = await apiClient.callApi(getSoilSampleDatasetApi, {
      orgCode,
      fieldId,
      datasetId: samplingEventId
    })
    const outline = outlineOf(boundary)
    const surface = interpolateSoilSurface({
      samples: dataset.collections.features,
      measurement,
      boundary: outline
    })
    if (surface === null) return { ok: false, reason: ZoneSourceFailure.Uninterpolable }

    return rangesLayer({
      boundary,
      zoning: {
        kind: ZoneSourceKind.SoilSample,
        values: soilSurfaceValues({ surface, boundary: outline }),
        surface,
        outline
      }
    })
  }

  const layer = useQuery({
    queryKey: ['zoneTuning', 'layer', ...sourceKey],
    queryFn: async (): Promise<ZoneLayerResult> => {
      const boundary = await fetchBoundary()

      switch (source.kind) {
        case ZoneSourceKind.AsApplied:
          return await stitchLayer({ boundary, ...source })
        case ZoneSourceKind.SoilSample:
          return await soilSampleLayer({ boundary, ...source })
        case ZoneSourceKind.SoilSurvey:
          return soilSurveyLayer({
            boundary,
            survey: await fetchSurvey(boundary),
            attribute: source.attribute
          })
      }
    }
  })

  const loaded = layer.data?.ok === true ? layer.data.layer : undefined
  const context = loaded?.kind === ZoneLayerKind.Ranges ? loaded : undefined

  const scoresQuery = ({
    scoringMethod,
    scoringZone
  }: {
    scoringMethod: BreakMethod
    scoringZone: SmallestZone
  }) => {
    const { simplify: _simplify, ...scoringSizes } = ZONE_SIZES[scoringZone]

    return {
      queryKey: ['zoneTuning', 'scores', ...sourceKey, scoringMethod, scoringZone],
      enabled: context !== undefined,
      queryFn: async (): Promise<CandidateScore[]> => {
        if (context === undefined) throw new Error('Layer not loaded')

        const candidates = candidateBreaks({ layer: context, method: scoringMethod })
        if (candidates.length === 0) return []

        const { zoning } = context
        if (zoning.kind !== ZoneSourceKind.AsApplied) {
          return candidates.map(({ zoneCount, breaks }) => ({
            zoneCount,
            breaks,
            ...localScore({ values: zoning.values, breaks })
          }))
        }

        const scored = await apiClient.callApi(
          lassoZoneScoresApi,
          {
            ...zoning.layerParams,
            ...scoringSizes,
            candidates: candidates.map(({ breaks }) => breaks)
          },
          zoning.lassoBody
        )

        return candidates.map(({ zoneCount, breaks }, index) => ({
          zoneCount,
          breaks,
          varianceExplained: scored.candidates[index].varianceExplained,
          reassignedShare: scored.candidates[index].reassignedFraction
        }))
      }
    }
  }

  const scores = useQuery({
    ...scoresQuery({ scoringMethod: method, scoringZone: smallestZone }),
    placeholderData: keepPreviousData
  })
  const startingScores = useQuery(
    scoresQuery({ scoringMethod: DEFAULT_BREAK_METHOD, scoringZone: DEFAULT_SMALLEST_ZONE })
  )

  const recommendedZoneCount = scores.isPlaceholderData ? undefined : recommend(scores.data)
  const startingZoneCount = recommend(startingScores.data)

  const zoneCountOf = (chosen: ZoneSelection) => {
    switch (chosen.kind) {
      case ZoneSelectionKind.Recommended:
        return startingZoneCount
      case ZoneSelectionKind.Count:
        return chosen.zoneCount
      case ZoneSelectionKind.Custom:
        return chosen.breaks.length + 1
    }
  }
  const selectedZoneCount = zoneCountOf(selection)

  const chooseBreaks = (histogram: ValueHistogram) => {
    if (selection.kind === ZoneSelectionKind.Custom) {
      return zonesForBreaks({ histogram, breaks: selection.breaks })
    }
    if (selectedZoneCount === undefined) return undefined
    return zoneBreaks({ histogram, zoneCount: selectedZoneCount, method })
  }

  const selectBreaks = () => {
    if (context === undefined) return undefined
    const chosen = chooseBreaks(context.histogram)
    return chosen === undefined ? undefined : betweenReadings({ layer: context, breaks: chosen })
  }

  const selectedBreaks = selectBreaks()
  const selectedZones = selectedBreaks?.ok === true ? selectedBreaks.zones : undefined
  const selectedValues = selectedZones === undefined ? [] : breakValues(selectedZones)

  const { simplify, ...zoneSizes } = ZONE_SIZES[smallestZone]
  const smooth = ROUNDING_PASSES[edges]

  const zoneRanges = async ({
    zoning,
    breaks
  }: {
    zoning: Zoning
    breaks: number[]
  }): Promise<ZonedFeatures> => {
    switch (zoning.kind) {
      case ZoneSourceKind.AsApplied: {
        const zoned = await apiClient.callApi(
          lassoZonesApi,
          { ...zoning.layerParams, ...zoneSizes, simplify, smooth, breaks },
          zoning.lassoBody
        )
        return {
          features: [...breaks, undefined].map((_, index) =>
            zoned.zones.features.filter((feature) => feature.properties.zone === index)
          ),
          score: {
            varianceExplained: zoned.varianceExplained,
            reassignedShare: zoned.reassignedFraction
          }
        }
      }
      case ZoneSourceKind.SoilSample:
        return {
          features: zoneSoilSurface({ surface: zoning.surface, boundary: zoning.outline, breaks }),
          score: localScore({ values: zoning.values, breaks })
        }
      case ZoneSourceKind.SoilSurvey:
        return {
          features: zoneNccpi({ survey: zoning.survey, breaks }),
          score: localScore({ values: zoning.values, breaks })
        }
    }
  }

  const preview = useQuery({
    queryKey: ['zoneTuning', 'preview', ...sourceKey, smallestZone, edges, selectedValues],
    enabled:
      loaded?.kind === ZoneLayerKind.Classes ||
      (context !== undefined && selectedZones !== undefined),
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<PlacedZones> => {
      if (loaded?.kind === ZoneLayerKind.Classes) {
        const classAcres = loaded.classes.map(({ features }) => acresOf(features))
        const placed = await placeZones({
          boundary: loaded.boundary,
          baseIndex: classAcres.indexOf(Math.max(...classAcres)),
          zones: loaded.classes.map(({ features }) => features)
        })
        return {
          featureCollection: placed.featureCollection,
          zones: loaded.classes.map(({ entry }, index) => ({
            key: placed.keys[index],
            acres: placed.acres[index],
            label: { kind: ZoneLabelKind.Class, entry }
          })),
          unit: undefined,
          score: null
        }
      }

      if (context === undefined || selectedZones === undefined) {
        throw new Error('Zones not selected')
      }

      const zoned = await zoneRanges({ zoning: context.zoning, breaks: selectedValues })
      const placed = await placeZones({
        boundary: context.boundary,
        baseIndex: Math.ceil(selectedZones.length / 2) - 1,
        zones: zoned.features
      })

      return {
        featureCollection: placed.featureCollection,
        zones: selectedZones.map(({ min, max }, index) => ({
          key: placed.keys[index],
          acres: placed.acres[index],
          label: { kind: ZoneLabelKind.Range, min, max }
        })),
        unit: context.unit,
        score: zoned.score
      }
    }
  })

  return {
    layer,
    layerKind: loaded?.kind,
    unit: context?.unit,
    histogram: context?.histogram,
    scores,
    recommendedZoneCount,
    selectedZoneCount,
    selectedZones,
    preview
  }
}

type PlacedZones = {
  featureCollection: FeatureCollection<PolyLikeGeometry>
  zones: { key: string | undefined; acres: number; label: ZoneLabel }[]
  unit: string | undefined
  score: ZoneScore | null
}

const localScore = ({
  values,
  breaks
}: {
  values: WeightedValue[]
  breaks: number[]
}): ZoneScore => ({ varianceExplained: varianceExplained({ values, breaks }), reassignedShare: 0 })

const placeZones = async (request: PlaceZonesRequest) => {
  const worker = new Worker(workerUrl, { type: 'module' })
  try {
    return await Comlink.wrap<WorkerApi>(worker).placeZones(request)
  } finally {
    worker.terminate()
  }
}

export const useIsPlacingZones = () =>
  useIsFetching({
    predicate: ({ queryKey: [scope, kind] }) =>
      scope === 'zoneTuning' && (kind === 'layer' || kind === 'preview')
  }) > 0
